import type { Definition } from '~/types'
import imageSizes from '../../constants/imageSizes'

const PageProductUpdates: Definition = {
  contentType: 'PageProductUpdates',
  fields: [
    {
      id: 'subtitle',
      name: 'Title Subheader',
      type: 'Symbol'
    },
    {
      id: 'announcement',
      name: 'Announcement',
      type: 'BlockHighlight',
      imageOptions: {
        resize: true,
        sizes: {
          small: { width: 344, height: 258, aspectRatio: '4/3' },
          medium: { width: 333, height: 333, aspectRatio: '1/1' },
          large: { width: 696, height: 522, aspectRatio: '4/3' }
        },
        lazy: true
      }
    },
    {
      id: 'productFilterLabel',
      name: 'Product Filter Label',
      type: 'Symbol'
    },
    {
      id: 'businessTypeFilterLabel',
      name: 'Business Type Filter Label',
      type: 'Symbol'
    },
    {
      id: 'dateFilterLabel',
      name: 'Date Filter Label',
      type: 'Symbol'
    },
    {
      id: 'resultsTitle',
      name: 'Results Title (for current month)',
      type: 'Symbol'
    },
    // @TODO Validate if a label for mobile is needed
    {
      id: 'scrollToNewsletterTitle',
      name: 'Scroll to newsletter Title',
      type: 'Symbol'
    },
    {
      id: 'scrollToNewsletterDescription',
      name: 'Scroll to newsletter Description',
      type: 'Symbol'
    },
    {
      id: 'olderPosts',
      name: 'Older posts',
      type: 'Symbol'
    },
    {
      id: 'newerPosts',
      name: 'Newer posts',
      type: 'Symbol'
    },
    {
      id: 'noResults',
      name: 'No Results',
      type: 'Symbol'
    },
    {
      id: 'form',
      name: 'Newsletter Form',
      type: 'BlockForm'
    }
  ]
}

const PageProductUpdatesDetail: Definition = {
  contentType: 'PageProductUpdatesDetail',
  fields: [
    {
      id: 'description',
      name: 'Description',
      type: 'Symbol'
    },
    {
      id: 'date',
      name: 'Date',
      type: 'Date',
      required: true
    },
    {
      id: 'products',
      name: 'Product Tags',
      type: 'Array',
      subType: 'ProductUpdatesProduct'
    },
    {
      id: 'businessType',
      name: 'Business Type',
      type: 'Array',
      subType: 'GlobalDataBusinessType',
      validations: [{ size: { min: 0, max: 5 } }]
    },
    {
      id: 'countryAvailability',
      name: 'Country Availability',
      type: 'Array',
      subType: 'GlobalDataCountry',
      validations: [{ size: { min: 0, max: 50 } }]
    },
    {
      id: 'image',
      name: 'Image',
      type: 'ModuleMedia',
      imageOptions: { resize: true, sizes: imageSizes.columns12_169, lazy: true }
    },
    {
      id: 'content',
      name: 'Content',
      type: 'RichText'
    },
    {
      id: 'integrationTypeDescription',
      name: 'Integration Type Description (modal)',
      type: 'Symbol'
    },
    {
      id: 'terminalRequirementsDescription',
      name: 'Terminal Requirements Description (modal)',
      type: 'Symbol'
    },
    {
      id: 'otherTitle',
      name: 'Other Title (modal)',
      type: 'Symbol'
    },
    {
      id: 'otherDescription',
      name: 'Other Description (modal)',
      type: 'Symbol'
    },
    {
      id: 'cta',
      name: 'Call to action',
      type: 'Array',
      subType: 'BlockCallToAction',
      validations: [{ size: { min: 0, max: 2 } }]
    }
  ]
}

const PageProductUpdatesTheSpotlight = {
  contentType: 'PageProductUpdatesTheSpotlight',
  fields: [
    {
      id: 'titlePlatforms',
      name: 'Platforms Title',
      type: 'Symbol'
    },
    {
      id: 'titleEnterprise',
      name: 'Enterprise Title',
      type: 'Symbol'
    },
    {
      id: 'hero',
      name: 'Hero',
      type: 'BlockContent'
    },
    {
      id: 'contentBlock1Platforms',
      name: 'ContentBlock1 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent'
    },
    {
      id: 'contentBlock1Enterprise',
      name: 'ContentBlock1 - Enterprise',
      type: 'BlockGroup',
      subType: 'BlockContent'
    },
    {
      id: 'quoteGroup1Platforms',
      name: 'Quote Group 1 - Platforms',
      type: 'BlockSimpleQuote'
    },
    // @TODO remove from the CMS (deprecated)
    // {
    //   id: 'quoteGroup1Enterprise',
    //   name: 'Quote Group 1 - Enterprise',
    //   type: 'BlockQuoteGroup'
    // },
    {
      id: 'contentBlock2Platforms',
      name: 'Content Block 2 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'contentBlock2Enterprise',
      name: 'Content Block 2 - Enterprise',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'contentBlock3Platforms',
      name: 'Content Block 3 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'contentBlock3Enterprise',
      name: 'Content Block 3 - Enterprise',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'quoteGroup2Platforms',
      name: 'Quote Group 2 - Platforms',
      type: 'BlockSimpleQuote'
    },
    // @TODO remove from the CMS (deprecated)
    // {
    //   id: 'quoteGroup2Enterprise',
    //   name: 'Quote Group 2 - Enterprise',
    //   type: 'BlockSimpleQuote'
    // },
    {
      id: 'contentBlock4Platforms',
      name: 'Content Block 4 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'contentBlock4Enterprise',
      name: 'Content Block 4 - Enterprise',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'contentBlock5Platforms',
      name: 'Content Block 5 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_43 }
    },
    {
      id: 'contentBlock6Platforms',
      name: 'Content Block 6 - Platforms',
      type: 'BlockGroup',
      subType: 'BlockContent',
      imageOptions: { resize: true, sizes: imageSizes.columns4_169 }
    },
    {
      id: 'videoEnterprise',
      name: 'Enterprise Video',
      type: ['ModuleVideo', 'ModuleInternalVideo'],
      imageOptions: { resize: true, sizes: imageSizes.spotlight_video }
    },
    {
      id: 'videoPlatforms',
      name: 'Platforms Video',
      type: ['ModuleVideo', 'ModuleInternalVideo'],
      imageOptions: { resize: true, sizes: imageSizes.spotlight_video }
    }
  ]
}

export default {
  PageProductUpdates,
  PageProductUpdatesDetail,
  PageProductUpdatesTheSpotlight
}
